import Header from "@/atomic/atoms/Header";
import { PropsView } from "./Models";
import Button from "@/atomic/atoms/Button";
import Modal from "@/atomic/atoms/Modal";
import Loading from "@/atomic/atoms/Loading";

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg
        width={24}
        height={24}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#909090"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#909090"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon({ checked }: { checked: boolean }) {
  const color = checked ? "var(--color-success)" : "#C4C4C4";
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M3.5 8.5L6.5 11.5L12.5 4.5"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const inputClass =
  "text-base text-text w-full bg-white border border-border rounded-xl px-4 h-14 pr-12 font-primary " +
  "placeholder:text-tertiary outline-none focus:ring-0 focus:border-primary transition-colors";

export default function PasswordView({
  goBack,
  submit,
  isDisabled,
  validatesNewPassword,
  isVisiblePassword,
  isVisibleNewPassword,
  toggleIsVisiblePassword,
  toggleIsVisibleNewPassword,
  isLoading,
  error,
  setError,
  success,
  goToLogin,
  refPassword,
  refNewPassword,
  onInput,
}: PropsView) {
  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header title="Alterar senha" hasLeftButton goBack={goBack} />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-6 pt-6 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={submit}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <h2 className="text-text text-[22px] leading-tight font-primary font-semibold tracking-tight">
              Alterar sua senha
            </h2>
            <p className="text-tertiary text-sm font-primary mt-1">
              Informe seus dados nos campos abaixo
            </p>

            <div className="mt-6 flex flex-col gap-5">
              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Senha atual
                </label>
                <div className="relative">
                  <input
                    ref={refPassword}
                    required
                    onInput={onInput}
                    type={isVisiblePassword ? "text" : "password"}
                    name="password"
                    placeholder="Sua senha atual"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={toggleIsVisiblePassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <EyeIcon visible={isVisiblePassword} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Nova senha
                </label>
                <div className="relative">
                  <input
                    ref={refNewPassword}
                    required
                    onInput={onInput}
                    type={isVisibleNewPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="Nova senha"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={toggleIsVisibleNewPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <EyeIcon visible={isVisibleNewPassword} />
                  </button>
                </div>

                <div className="mt-3 bg-white w-full rounded-xl border border-border p-4 space-y-2">
                  {validatesNewPassword?.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckIcon checked={item.success} />
                      <span
                        className={`text-sm font-primary ${
                          item.success
                            ? "text-success font-semibold"
                            : "text-tertiary"
                        }`}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 shrink-0">
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold border-0"
              title="Avançar"
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </form>
      </main>
      {error && (
        <Modal
          title="Ops!"
          message="Houve um problema ao trocar sua senha, quer tentar novamente?"
          actionMessage="Tentar novamente"
          action={submit}
          actionSecondMessage="Não!"
          actionSecond={() => setError(false)}
        />
      )}
      {success && (
        <Modal
          title="Muito bem!"
          message="Sua senha foi alterada com sucesso. Realize o login novamente para continuar usando o Pedágio Eletrônico Sem Parar."
          actionMessage="Fazer Login"
          action={goToLogin}
        />
      )}
      {isLoading && <Loading />}
    </div>
  );
}
